import { PhoneValidationResult } from '@shared/core/sms'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { useQuery, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { ApiService } from 'src/services/api.service'

export const VALIDATE_PHONE_NUMBERS_CACHE_KEY = 'validate-phone-numbers'

export function useValidatePhoneNumbers(phoneNumbers: string[], options?: UseQueryOptions<PhoneValidationResult[]>) {
  const handleRetry = useHandleRetry()

  return useQuery([VALIDATE_PHONE_NUMBERS_CACHE_KEY, phoneNumbers], async () => validatePhoneNumbers(phoneNumbers), {
    enabled: phoneNumbers.length > 0,
    retry: handleRetry,
    cacheTime: 0,
    ...options,
  })
}

async function validatePhoneNumbers(phoneNumbers: string[]): Promise<PhoneValidationResult[]> {
  const preValidationResults = preValidatePhoneNumbers(phoneNumbers)
  const allValid = preValidationResults.every((result) => result.isValid)
  if (allValid) {
    // If all numbers appear valid, proceed with API call for additional checks
    return await ApiService.SmsCompliance.validatePhoneNumbers(phoneNumbers)
  } else {
    // If any numbers are invalid, return pre-validation results
    return preValidationResults
  }
}

/**
 * Pre-validates phone numbers using libphonenumber-js
 * Returns validation results in the same format as the API
 */
function preValidatePhoneNumbers(phoneNumbers: string[]): PhoneValidationResult[] {
  return phoneNumbers.map((phoneNumber) => {
    try {
      const parsed = parsePhoneNumberFromString(phoneNumber)
      if (parsed?.isValid()) {
        return {
          phoneNumber: parsed.number,
          countryCode: parsed.country?.toLowerCase() || null,
          isValid: true,
          invalidReason: null,
        }
      } else {
        return {
          phoneNumber,
          countryCode: null,
          isValid: false,
          invalidReason: 'format',
          errorMessage: 'Invalid phone number.',
        }
      }
    } catch (error) {
      return {
        phoneNumber,
        countryCode: null,
        isValid: false,
        invalidReason: 'format',
        errorMessage: 'Invalid phone number.',
      }
    }
  })
}
